import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type ProductionDeclarationSubmitDialogProps = {
  isPending: boolean;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  supplierName: string;
};

export function ProductionDeclarationSubmitDialog({
  isPending,
  onConfirm,
  onOpenChange,
  open,
  supplierName,
}: ProductionDeclarationSubmitDialogProps) {
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const [confirmed, setConfirmed] = useState(false);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setConfirmed(false);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent className='sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>{t('submitDialog.title')}</DialogTitle>
          <DialogDescription asChild>
            <div className='space-y-4 text-muted-foreground text-sm'>
              <p>
                {t('submitDialog.paragraph1', {
                  supplierName,
                  interpolation: { escapeValue: false },
                })}
              </p>
              <p>
                {t('submitDialog.paragraph2Prefix', {
                  supplierName,
                  interpolation: { escapeValue: false },
                })}{' '}
                <strong className='text-foreground'>
                  {t('submitDialog.paragraph2Bold')}
                </strong>
                .
              </p>
              <p>
                {t('submitDialog.paragraph3', {
                  supplierName,
                  interpolation: { escapeValue: false },
                })}
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className='flex cursor-pointer items-start gap-3 py-2'>
          <Checkbox
            checked={confirmed}
            className='mt-0.5'
            disabled={isPending}
            id='submit-dialog-confirm'
            onCheckedChange={(checked) => setConfirmed(checked === true)}
          />
          <label
            className='font-semibold text-sm leading-snug'
            htmlFor='submit-dialog-confirm'
          >
            {t('submitDialog.checkboxLabel')}
          </label>
        </div>
        <div className='flex w-full justify-end'>
          <Button
            disabled={!confirmed || isPending}
            onClick={onConfirm}
            type='button'
            variant='declaration-primary'
          >
            {t('submitDialog.submit')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
